import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { customStyles } from '../../Utils/utils';
import { gpLogin } from '../../rtk-apis/gp/gpApi';
import { setGpSession, isValidGp } from '../../Utils/gpAuth';
import '../../styled/pages/login.scss';

/**
 * GP portal login — provider number + password.
 * Separate page and API from the normal login (full isolation).
 */
const GpLogin: React.FC = () => {
  const navigate = useNavigate();
  const [providerNumber, setProviderNumber] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isValidGp()) {
      navigate('/gp/reports');
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!providerNumber.trim() || !password) {
      setError('Provider number and password are required');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const result = await gpLogin(providerNumber.trim(), password);
      setGpSession(result.token, result.user);
      navigate('/gp/reports');
    } catch (err: any) {
      setError(err?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="login" style={customStyles.loginBackground}>
      <div className="login__container">
        <div className="login__wrap">
          <div className="branding">
            <div className="branding__info">
              <h1 className="title title--h3 title--normal">GP Portal</h1>
              <p className="para" style={{ marginTop: '8px', opacity: 0.85 }}>
                Sign in with your provider number to view reports addressed to you.
              </p>
            </div>
          </div>
          <div className="login__form">
            <form onSubmit={handleSubmit}>
              <div className="form__group">
                <div className="form__groupField">
                  <input
                    type="text"
                    autoComplete="username"
                    className="form__input"
                    placeholder="Provider number (e.g. 0410696F)"
                    value={providerNumber}
                    onChange={e => setProviderNumber(e.target.value)}
                  />
                </div>
              </div>
              <div className="form__group">
                <div className="form__groupField">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    className="form__input"
                    placeholder="Password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ paddingRight: '8px' }}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              {error && (
                <div className="form__error">
                  <p className="mb-2 text-base">{error}</p>
                </div>
              )}

              <div className="form__button">
                <button className="btn btn__primary btn--full" type="submit" disabled={loading}>
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </div>

              <p className="para para--center" style={{ marginTop: '16px' }}>
                <button type="button" className="link" onClick={() => navigate('/login')}>
                  Not a GP? Go to staff login
                </button>
              </p>
              <p className="para para--center" style={{ marginTop: '8px', fontSize: '12px', opacity: 0.7 }}>
                No account or forgotten password? Contact the clinic that sends you reports.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GpLogin;
